"use client";

import React, { useState } from 'react';
import Image from 'next/image';
import { 
  Shield, 
  Users, 
  Building, 
  FileText,
  Award,
  TrendingUp,
  Handshake
} from 'lucide-react';
import ConsultationModal from '@/components/public/ConsultationModal';
import PartnerCarousel from '@/components/PartnerCarousel';

export default function InsurancePage() {
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  // NOTE: every CTA that calls this sits in commented-out sections below —
  // the consultation modal is currently unreachable on this page (matches
  // the pre-existing no-unused-vars lint warning). Analytics intentionally
  // not wired here until a live opener exists; see analytics.ctaClicked
  // call sites on the home page and scroll prompt for the pattern.
  const openConsultationModal = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsConsultationModalOpen(true);
  };
  const closeConsultationModal = () => setIsConsultationModalOpen(false);

  // Partner data for the carousel - from Groundwork Insurance website
  const partners = [
    { id: '1', name: 'Ashlar Homes', logo: '/assets/partners/ashlar-homes-logo-bw.png' },
    { id: '2', name: 'Benchmark Homes', logo: '/assets/partners/benchmark-homes.jpg' },
    { id: '3', name: 'Benton Homes', logo: '/assets/partners/benton-logo.jpg' },
    { id: '4', name: 'Cannon Builders', logo: '/assets/partners/cannon-builders.jpg' },
    { id: '5', name: 'Claymont Development', logo: '/assets/partners/claymont-development.jpg' },
    { id: '6', name: 'CMS Homes', logo: '/assets/partners/cms-homes.jpg' },
    { id: '7', name: 'Consort Homes', logo: '/assets/partners/consort-homes.png' },
    { id: '8', name: 'Cross Homes', logo: '/assets/partners/cross-homes-logo.jpg' },
    { id: '9', name: 'F&F Logo', logo: '/assets/partners/fandf-logo.jpg' },
    { id: '10', name: 'Freeman Homes', logo: '/assets/partners/freeman-homes.jpg' },
    { id: '11', name: 'GSell Homes', logo: '/assets/partners/gsell-homes.jpg' },
    { id: '12', name: 'Hardesty Homes', logo: '/assets/partners/hardesty-homes.jpg' },
    { id: '13', name: 'Hibbs Homes', logo: '/assets/partners/hibbs-homes.jpg' },
    { id: '14', name: 'Houston Homes', logo: '/assets/partners/houston-homes-logo.jpg' },
    { id: '15', name: 'Ivie League Homes', logo: '/assets/partners/ivie-league-homes.jpg' },
    { id: '16', name: 'Kemp Homes', logo: '/assets/partners/kemp-homes.jpg' },
    { id: '17', name: 'Kingbridge', logo: '/assets/partners/kingbridge-logo.jpg' },
    { id: '18', name: 'Lindbergh', logo: '/assets/partners/lindbergh.jpg' },
    { id: '19', name: 'Lombardo Homes', logo: '/assets/partners/lombardo-gray.jpg' },
    { id: '20', name: 'LUXE Real Estate', logo: '/assets/partners/luxe-real-estate.jpg' },
    { id: '21', name: 'McKelvey Homes', logo: '/assets/partners/mckelvey-homes.jpg' },
    { id: '22', name: 'NJL Vector', logo: '/assets/partners/njl-vector-logo.jpg' },
    { id: '23', name: 'Prestige Homes', logo: '/assets/partners/prestige-logo-new2.jpg' },
    { id: '24', name: 'Renaissance Living', logo: '/assets/partners/renaissance-living.jpg' },
    { id: '25', name: 'Rolwes Homes', logo: '/assets/partners/rolwes-homes.jpg' },
    { id: '26', name: 'Rombach Homes', logo: '/assets/partners/rombach-homes.png' },
    { id: '27', name: 'TR Hughes Homes', logo: '/assets/partners/tr-hughes-homes.jpg' },
    { id: '28', name: 'UIC Homes', logo: '/assets/partners/uic-homes.jpg' },
    { id: '29', name: 'Vanderbilt Homes', logo: '/assets/partners/vanderbilt-homes.png' },
    { id: '30', name: 'Hearthside', logo: '/assets/partners/hearthside-1.png' },
    { id: '31', name: 'Flower and Fendler', logo: '/assets/partners/flowerandfendler.png' },
  ];

  return (
    <main className="flex min-h-screen flex-col">
      {/* Hero Section - Two Column Layout */}
      <section className="flex flex-col md:flex-row w-full">
        {/* Home Building Section */}
        <div className="relative w-full md:flex-1 h-[280px] sm:h-[320px] md:h-[480px] lg:h-[520px] overflow-hidden group">
          <div className="absolute inset-0">
            <Image
              src="/assets/insurance/builder-background.jpg"
              alt="Home Building Insurance"
              fill
              className="object-cover"
              sizes="(min-width: 1024px) 50vw, (min-width: 768px) 50vw, 100vw"
              priority
            />
          </div>
          {/* Text + Icons Overlay */}
          <div className="absolute inset-0 z-10 flex items-center justify-center px-4">
            <div className="flex flex-col items-center gap-3 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow">
                Home Building
              </h2>
              <p className="text-white/90 text-sm md:text-base max-w-xl">
                Protect your business assets.
              </p>
              <div className="hidden md:flex gap-4 items-center">
              <div>
                <Image
                  src="/assets/insurance/subs.png"
                  alt="Sub-Contractors"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
              <div>
                <Image
                  src="/assets/insurance/supplies.png"
                  alt="Supplies"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
              <div>
                <Image
                  src="/assets/insurance/hoas.png"
                  alt="HOAs"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
              <div>
                <Image
                  src="/assets/insurance/equipment.png"
                  alt="Equipment"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
              <div>
                <Image
                  src="/assets/insurance/builders-risk.png"
                  alt="Builders Risk"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Home Owners Section */}
        <div className="relative w-full md:flex-1 h-[280px] sm:h-[320px] md:h-[480px] lg:h-[520px] overflow-hidden group">
          <div className="absolute inset-0">
            <Image
              src="/assets/insurance/home-background.jpg"
              alt="Home Owners Insurance"
              fill
              className="object-cover"
              sizes="(min-width: 1024px) 50vw, (min-width: 768px) 50vw, 100vw"
              priority
            />
          </div>
          {/* Text + Icons Overlay */}
          <div className="absolute inset-0 z-10 flex items-center justify-center px-4">
            <div className="flex flex-col items-center gap-3 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow">
                Home Owners
              </h2>
              <p className="text-white/90 text-sm md:text-base max-w-xl">
                Protect the things you love.
              </p>
              <div className="hidden md:flex gap-4 items-center">
              <div>
                <Image
                  src="/assets/insurance/home.png"
                  alt="Home"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
              <div>
                <Image
                  src="/assets/insurance/auto.png"
                  alt="Auto"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
              <div>
                <Image
                  src="/assets/insurance/rv.png"
                  alt="RV"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
              <div>
                <Image
                  src="/assets/insurance/moto.png"
                  alt="Motorcycle"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
              <div>
                <Image
                  src="/assets/insurance/life.png"
                  alt="Life Insurance"
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>



      {/* Better Insurance Starts Here Section */}
      <section className="py-16" style={{ backgroundColor: '#0B253C' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <h2 className="text-4xl font-bold mb-4 text-white">
              Better Insurance Starts Here.
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Our people-first approach ensures you'll always receive the best possible service. 
              With decades of experience serving the new construction industry, we know what it takes 
              to keep our clients happy, and we look forward to doing the same for you.
            </p>
          </div>

          <div className="mt-10 rounded-xl bg-muted p-6 md:p-8">
            <p className="text-center mb-4 text-3xl font-bold" style={{ color: '#0B253C' }}>
              Choose Your Insurance Line
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <a
              href="https://groundworkinsurance.formstack.com/forms/auto_insurance"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 rounded-lg text-center transition-colors cursor-pointer block hover:opacity-90 w-full"
              style={{ backgroundColor: '#0B253C' }}
            >
              <Users className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-4">Personal Lines</h3>
              <span className="inline-flex items-center justify-center rounded-lg border-2 border-white bg-transparent px-4 py-2 text-sm font-semibold text-white">
                Get Quote
              </span>
            </a>
            <a
              href="https://groundworkinsurance.formstack.com/forms/residential_home_builders_liability"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 rounded-lg text-center transition-colors cursor-pointer block hover:opacity-90 w-full"
              style={{ backgroundColor: '#0B253C' }}
            >
              <Building className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-4">Home Builder</h3>
              <span className="inline-flex items-center justify-center rounded-lg border-2 border-white bg-transparent px-4 py-2 text-sm font-semibold text-white">
                Get Quote
              </span>
            </a>
            <a
              href="https://groundworkinsurance.formstack.com/forms/subcontractor_insurance_quote_request"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 rounded-lg text-center transition-colors cursor-pointer block hover:opacity-90 w-full"
              style={{ backgroundColor: '#0B253C' }}
            >
              <FileText className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-4">Sub Contractor</h3>
              <span className="inline-flex items-center justify-center rounded-lg border-2 border-white bg-transparent px-4 py-2 text-sm font-semibold text-white">
                Get Quote
              </span>
            </a>
            <a
              href="https://groundworkinsurance.formstack.com/forms/builders_risk"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 rounded-lg text-center transition-colors cursor-pointer block hover:opacity-90 w-full"
              style={{ backgroundColor: '#0B253C' }}
            >
              <Shield className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-4">Builders Risk</h3>
              <span className="inline-flex items-center justify-center rounded-lg border-2 border-white bg-transparent px-4 py-2 text-sm font-semibold text-white">
                Get Quote
              </span>
            </a>
            <a
              href="https://groundworkinsurance.formstack.com/forms/commercial_lines"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 rounded-lg text-center transition-colors cursor-pointer block hover:opacity-90 w-full"
              style={{ backgroundColor: '#0B253C' }}
            >
              <Handshake className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-4">Commercial Lines</h3>
              <span className="inline-flex items-center justify-center rounded-lg border-2 border-white bg-transparent px-4 py-2 text-sm font-semibold text-white">
                Get Quote
              </span>
            </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Partners Section */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#0B253C' }}>
              Proud to Have Served The Customers Of:
            </h2>
          </div>
          <PartnerCarousel items={partners} className="py-4" />
        </div>
      </section>

      {/* Your New Home Industry Insurance Advisor Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#0B253C' }}>
                Your new home industry insurance advisor.
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Groundwork Insurance, a Builder Collaborative Company, is an independent insurance broker with a vision of serving
                the specific needs of the St. Louis and Kansas City residential new construction industry, 
                including homebuilders, subcontractors, vendors, suppliers, and the consumers who ultimately 
                make the decision to purchase a newly built home.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We take great pride in our agency, community, and customers. As an independent agency, 
                we offer choices, provide a high level of service, and are local to the community.
              </p>
            </div>
            <div className="relative flex justify-center">
              <Image
                src="/assets/logo/groundwork_insurance_logo.jpg"
                alt="Groundwork Insurance"
                width={1280}
                height={900}
                className="w-[85%] h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#0B253C' }}>
              Why Choose Us?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 border rounded-lg">
              <TrendingUp className="w-12 h-12 text-primary mx-auto mb-4" style={{ color: '#0B253C' }} />
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#0B253C' }}>The Power of Choice</h3>
              <p className="text-gray-600">
                As an independent agency that focuses on the residential new construction industry, 
                we offer a choice of multiple insurance carriers, coverages, prices, and more. 
                Our primary focus is to find the insurance coverage that best suits our customer's 
                unique needs and gives the best options.
              </p>
            </div>
            
            <div className="text-center p-6 border rounded-lg">
              <Award className="w-12 h-12 text-primary mx-auto mb-4" style={{ color: '#0B253C' }} />
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#0B253C' }}>Leverage Our Experience</h3>
              <p className="text-gray-600">
                As long-time members of the Home Builders Association deeply rooted in the industry, 
                members of the Builder Collaborative team have been professionally serving the risk 
                management and insurance needs of large-scale home builders and the customers choosing 
                to purchase a newly built home for decades.
              </p>
            </div>
            
            <div className="text-center p-6 border rounded-lg">
              <Handshake className="w-12 h-12 text-primary mx-auto mb-4" style={{ color: '#0B253C' }} />
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#0B253C' }}>Our Consultative Approach</h3>
              <p className="text-gray-600">
                From business owners to homeowners, we all have backgrounds, circumstances, and 
                attributes which need to be uniquely insured. Our hands-on approach, guided by 
                20 years of industry experience, allows Builder Collaborative to produce remarkable 
                outcomes specific to each client.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Types Section */}
      {/* <section className="py-16" style={{ backgroundColor: '#0B253C' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">
              What types of coverage are you interested in?
            </h2>
            <p className="text-lg text-gray-300">
              We offer comprehensive insurance solutions for all your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white/10 p-6 rounded-lg text-center hover:bg-white/20 transition-colors cursor-pointer">
              <Building className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Home</h3>
              <p className="text-gray-300 text-sm">Comprehensive home insurance coverage</p>
            </div>
            
            <div className="bg-white/10 p-6 rounded-lg text-center hover:bg-white/20 transition-colors cursor-pointer">
              <Shield className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Auto</h3>
              <p className="text-gray-300 text-sm">Auto insurance for all vehicle types</p>
            </div>
            
            <div className="bg-white/10 p-6 rounded-lg text-center hover:bg-white/20 transition-colors cursor-pointer">
              <Users className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Life</h3>
              <p className="text-gray-300 text-sm">Life insurance protection for your family</p>
            </div>
            
            <div className="bg-white/10 p-6 rounded-lg text-center hover:bg-white/20 transition-colors cursor-pointer">
              <FileText className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Umbrella</h3>
              <p className="text-gray-300 text-sm">Additional liability protection</p>
            </div>
            
            <div className="bg-white/10 p-6 rounded-lg text-center hover:bg-white/20 transition-colors cursor-pointer">
              <Calculator className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Recreational Vehicles</h3>
              <p className="text-gray-300 text-sm">Insurance for boats, RVs, and more</p>
            </div>
            
            <div className="bg-white/10 p-6 rounded-lg text-center hover:bg-white/20 transition-colors cursor-pointer">
              <CheckCircle className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Other Coverage</h3>
              <p className="text-gray-300 text-sm">Customized insurance solutions</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Maverick Warranty Section */}
      {/* <section className="py-16 bg-gradient-to-r from-primary/10 to-secondary/20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#0B253C' }}>
                Maverick Warranty
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Partner with Maverick Warranty for comprehensive protection on your new construction projects. 
                Our warranty solutions provide peace of mind for builders and homeowners alike.
              </p>
              <div className="flex gap-4">
                <button 
                  onClick={openConsultationModal}
                  className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#0B253C', color: 'white' }}
                >
                  Learn More
                </button>
              </div>
            </div>
            <div className="relative">
              <Image
                src="/innovative-solutions.jpg"
                alt="Innovative solutions"
                width={600}
                height={400}
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section> */}

      {/* Contact Section */}
      {/* <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4" style={{ color: '#0B253C' }}>
            Contact Us Today!
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Ready to get started with better insurance coverage? Our team is here to help you 
            find the perfect solution for your needs.
          </p>
          
          <div className="flex gap-4 justify-center mb-12">
            <button 
              onClick={openConsultationModal}
              className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#0B253C', color: 'white' }}
            >
              Get a Quote
            </button>
            <button className="border border-primary text-primary px-8 py-3 rounded-lg font-semibold hover:bg-primary/5 transition-colors">
              Schedule Consultation
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-muted/30 p-6 rounded-lg">
              <Phone className="w-8 h-8 text-primary mx-auto mb-4" style={{ color: '#0B253C' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#0B253C' }}>Call Us</h3>
              <p className="text-gray-600 mb-2">(636) 449-5200</p>
              <p className="text-sm text-gray-500">Monday - Friday, 8AM - 6PM</p>
            </div>
            
            <div className="bg-muted/30 p-6 rounded-lg">
              <Mail className="w-8 h-8 text-primary mx-auto mb-4" style={{ color: '#0B253C' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#0B253C' }}>Email Us</h3>
              <p className="text-gray-600 mb-2">Info@BuilderCollab.com</p>
              <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
            </div>
          </div>
          
          <div className="mt-12 p-6 bg-muted/30 rounded-lg max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#0B253C' }}>Office Location</h3>
            <p className="text-gray-600">687 Trade Center Blvd. Suite 120</p>
            <p className="text-gray-600">Chesterfield MO 63005</p>
          </div>
        </div>
      </section> */}

      {/* Legal Disclaimer Footer */}
      <footer className="bg-white pt-8">
        <div className="container mx-auto px-4">
          <div className="border-t-4 mb-6" style={{ borderTopColor: '#0B253C' }}></div>
          <div className="text-xs leading-relaxed space-y-2" style={{ color: '#0B253C' }}>
            <p style={{ color: '#0B253C' }}>
              The Builder Collaborative™, Framework Mortgage, LLC d/b/a Groundwork Mortgage (“GWM”) and Groundwork Insurance Services, LLC d/b/a Groundwork Insurance (“GWI”) share common ownership through a parent limited liability company. The Builder Collaborative is not a mortgage lender or insurance agency and does not originate loans or sell, solicit or negotiate insurance. Mortgage products are offered through GWM, and insurance products are offered through GWI. Because of this relationship, a referral among these companies may provide the referring company a financial or other benefit. GWM may also have advertising relationships with homebuilders. You are not required to use GWM or GWI as a condition of receiving any Builder Collaborative service, of purchasing a home from a builder, or of obtaining a loan from GWM. Other mortgage companies and insurance agencies offer similar services, and the choice is entirely yours. We encourage you to shop around to make sure you are receiving the service and rates that best fit your needs.
            </p>
            <div className="mt-4 pt-4 border-t border-gray-300">
              <p className="text-center" style={{ color: '#0B253C' }}>
                <strong>Groundwork Insurance Services, LLC d/b/a Groundwork Insurance</strong>: Missouri Insurance License #8366130, NPN 17798818. 687 Trade Center Blvd, Suite 120, Chesterfield, MO 63005.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Consultation Modal */}
      <ConsultationModal 
        isOpen={isConsultationModalOpen}
        onClose={closeConsultationModal}
      />
    </main>
  );
}